<template>
    <pc-capsule
        v-bind="capsuleBinder"
        v-bind:title="attachmentsCapsuleTitle"
        v-bind:has-body="isAdminPage && canAddRemoveAttachment"
        data-test-role="attachments-capsule"
        has-table
        v-on="capsuleListeners">
        <template v-slot:capsule-table>
            <pc-loader v-bind:loading="tableLoading">
                <pc-table
                    id="AttachmentTable"
                    v-bind:items="attachments"
                    v-bind:columns="columns"
                    v-bind:initial-sort="[[0, 'desc']]"
                    v-bind:empty-text="emptyTableMessage"
                    v-bind:card-property-cutoff="2"
                    v-bind:hide-actions="!isAdminPage"
                    key-prop="AttachmentId"
                    hide-search
                    card-show-empty-placeholder>
                    <template v-slot:AttachmentDefinitionName="attachment">
                        <attachment-definition-column
                            v-bind:is-admin-page="isAdminPage"
                            v-bind:wide-view="wideView"
                            v-bind:attachment="attachment"
                            v-on:view-attachment="openViewAttachmentPage(attachment)"
                            v-on:download-attachment="downloadAttachment(attachment)" />
                    </template>
                    <template v-slot:FileName="attachment">
                        <file-name-column
                            v-bind:is-admin-page="isAdminPage"
                            v-bind:wide-view="wideView"
                            v-bind:attachment="attachment"
                            v-bind:show-remove="isAdminPage && canAddRemoveAttachment"
                            v-on:remove-attachment="confirmAndRemoveAttachment(attachment)"
                            v-on:view-attachment="openViewAttachmentPage(attachment)"
                            v-on:download-attachment="downloadAttachment(attachment)" />
                    </template>
                    <template v-slot:Note="attachment">
                        <attachment-note v-bind:note="attachment.Note" />
                    </template>
                    <template v-slot:Status="attachment">
                        <span
                            class="text-nowrap"
                            v-bind:class="getApprovalStatusDisplayClass(attachment.Status, true)"
                            data-test-role="attachment-status">
                            {{ getApprovalStatusDisplay(attachment.Status, true) }}
                        </span>
                    </template>
                    <template
                        v-if="isAdminPage"
                        v-slot:actionsRow="attachment">
                        <pc-button-row>
                            <pc-button
                                v-if="canAddRemoveAttachment && !wideView"
                                v-on:click="confirmAndRemoveAttachment(attachment)">
                                {{ removeBtnLabel }}
                            </pc-button>
                            <pc-button v-on:click="viewHistory(attachment)">
                                {{ viewHistoryBtnLabel }}
                            </pc-button>
                        </pc-button-row>
                    </template>
                </pc-table>
            </pc-loader>
        </template>
        <template v-slot:default>
            <div v-if="isAdminPage && canAddRemoveAttachment">
                <div v-if="!showAttachmentSelector">
                    <pc-button
                        link
                        class="p-0"
                        v-on:click="onAddAttachment">
                        {{ addAttachmentLinkText }}
                    </pc-button>
                </div>
                <div v-else>
                    <pc-loader v-bind:loading="attachmentDefinitionsLoading">
                        <div
                            v-if="hasAttachmentDefinitions"
                            class="row">
                            <div v-bind:class="responsiveClasses.attachmentSelector">
                                <pc-select
                                    v-model="selectedAttachmentDefinitionId"
                                    v-bind:options="attachmentDefinitions"
                                    text-prop="Name"
                                    value-prop="PublicId"
                                    v-bind:label="attachmentToAddLabel" />
                            </div>
                            <div v-bind:class="responsiveClasses.attachmentFile">
                                <pc-file
                                    v-bind:disabled="!selectedAttachmentDefinitionId"
                                    v-bind:model-value="document"
                                    v-bind:upload-function="uploadAndSaveAttachment"
                                    v-bind:extensions="selectedAttachmentDefinition.AllowedExtensions"
                                    v-bind:max-allowed-size-kb="selectedAttachmentDefinition.MaxAllowedSizeKb"
                                    v-bind:show-view="false"
                                    v-bind:label="selectAFileMessage"
                                    show-extensions
                                    v-on:upload-canceled="uploadCanceled" />
                            </div>
                        </div>
                        <div
                            v-else
                            class="fst-italic">
                            {{ noAttachmentElementsMessage }}
                        </div>
                        <div
                            v-if="!document"
                            class="d-flex justify-content-end">
                            <pc-button v-on:click="addAttachmentCancel">
                                {{ cancelText }}
                            </pc-button>
                        </div>
                    </pc-loader>
                </div>
            </div>
        </template>
    </pc-capsule>
</template>

<script>
    import capsuleBase, { WIDE_BP } from "Projects/SharedCapsules/_sharedCapsuleBase";
    import { XHR_CONTENT_TYPE, downloadFile } from "Util/util";
    import {
        getApprovalStatusDisplay,
        getApprovalStatusDisplayClass,
        ATTACHMENT_APPROVAL_STATUS,
        confirmViewSensitiveAttachment,
        ATTACHMENT_UPDATED_EVENT
    } from "Util/clientAttachment";
    import { getClientTimestampTableColumn } from "Util/clientDateTime";

    import attachmentHistory from "./attachmentHistory.vue";
    import attachmentNote from "./attachmentNote.vue";
    import fileNameColumn from "./fileNameColumn.vue";
    import attachmentDefinitionColumn from "./attachmentDefinitionColumn.vue";

    import { formatBasicUserInfoForDisplay } from "Util/clientUser";

    import { isModuleRegistered } from "SharedStores/util";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_NAMESPACE } from "Forms/Components/ProjectForm/projectFormControllerStore";
    import { KEYS, NAMESPACE } from "./i18n/attachmentsCapsule.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { mapI18n } from "Util/i18n";
    import { getResponsiveClassMap } from "Responsive/manager";
    import { useProjectLockStore } from "Projects/projectLockStore.js";

    const responsiveConfig = {
        attachmentSelector: {
            col: { xs: 12, [WIDE_BP.name]: 6 }
        },
        attachmentFile: {
            col: { xs: 12, [WIDE_BP.name]: 6 }
        }
    };

    export default {
        components: {
            attachmentDefinitionColumn,
            attachmentNote,
            fileNameColumn
        },
        extends: capsuleBase,
        props: {
            id: { type: String, required: false, default: "attachments" },
            model: { type: Object, required: true }
        },
        setup() {
            const projectLockStore = useProjectLockStore();
            return { projectLockStore };
        },
        data() {
            return {
                tableLoading: false,
                attachments: [],

                showAttachmentSelector: false,
                attachmentDefinitionsLoading: false,
                attachmentDefinitions: null,

                selectedAttachmentDefinitionId: null,
                document: null
            };
        },
        computed: {
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE),
            columns() {
                const baseCols = [
                    getClientTimestampTableColumn(this.uploadTimestampHeader, "UploadTimestamp"),
                    {
                        header: this.attachmentDefinitionNameHeader,
                        property: "AttachmentDefinitionName",
                        isCardHeader: true
                    },
                    {
                        header: this.filenameHeader,
                        property: "FileName",
                        columnClass: "attachments-capsule-filename-cell-wrapper"
                    },
                    {
                        header: this.noteHeader,
                        property: "Note",
                        columnClass: "attachments-capsule-note-cell-wrapper"
                    },
                    {
                        header: this.statusHeader,
                        property: "Status",
                        sortValue(item) {
                            return getApprovalStatusDisplay(item.Status, true);
                        },
                        isValueEmpty(item) {
                            return item == null || item === "" || item.Status === ATTACHMENT_APPROVAL_STATUS.pending;
                        }
                    },
                    {
                        header: this.statusChangeUserHeader,
                        property: "StatusChangeUser",
                        visible: this.isAdminPage,
                        displayValue: (user) => formatBasicUserInfoForDisplay(user, null, true),
                        sortValue: (item) => formatBasicUserInfoForDisplay(item.StatusChangeUser, null, true)
                    },
                    {
                        ...getClientTimestampTableColumn(this.statusChangeTimestampHeader, "StatusChangeTimestamp"),
                        visible: this.isAdminPage
                    }
                ];

                return baseCols.map((col) => {
                    col.orderable = true;
                    col.visible = col.visible == null ? true : col.visible;
                    return col;
                });
            },
            selectedAttachmentDefinition() {
                if (!this.hasAttachmentDefinitions || !this.selectedAttachmentDefinitionId) {
                    return {};
                }
                return this.attachmentDefinitions.find((ad) => {
                    return ad.PublicId === this.selectedAttachmentDefinitionId;
                });
            },
            canAddRemoveAttachment() {
                return this.$app.LoggedInRole.RolePrivileges.ProjectAdminAddAttachment;
            },
            responsiveClasses() {
                return getResponsiveClassMap(this.viewport, responsiveConfig);
            },
            controller() {
                return this.$serviceControllers.AttachmentsCapsule;
            }
        },
        created() {
            this._loadTableData();
        },
        methods: {
            formatBasicUserInfoForDisplay(clientLogin) {
                return formatBasicUserInfoForDisplay(clientLogin, null, true);
            },
            async _loadTableData() {
                try {
                    this.tableLoading = true;
                    const result = await this.controller.GetAttachments({
                        programId: this.ProgramId,
                        projectId: this.ProjectId,
                        isAdminPage: this.isAdminPage
                    });

                    this.attachments = result.Data.Attachments;
                } finally {
                    this.tableLoading = false;
                }
            },
            async downloadAttachment(attachment) {
                if (
                    attachment.AttachmentDefinitionIsSensitive &&
                    !(await this._confirmViewSensitiveAttachment(false))
                ) {
                    return;
                }

                const result = await this.controller.GetDownloadAttachmentUrl({
                    programId: this.ProgramId,
                    attachmentId: attachment.AttachmentId
                });
                downloadFile(result.Data);
            },
            async _confirmViewSensitiveAttachment(isView) {
                return await confirmViewSensitiveAttachment(this, isView);
            },
            async openViewAttachmentPage(attachment) {
                if (attachment.AttachmentDefinitionIsSensitive && !(await this._confirmViewSensitiveAttachment(true))) {
                    return;
                }

                const viewAttachmentTab = this.$app.openWindow(
                    `${this.model.ViewAttachmentUrl}&AttachmentId=${attachment.AttachmentId}`,
                    "_blank"
                );

                if (!viewAttachmentTab || !viewAttachmentTab.open || viewAttachmentTab.closed) {
                    this.$app.showErrorDialog(" ", this.disablePopupMessage);
                } else {
                    viewAttachmentTab.addEventListener(ATTACHMENT_UPDATED_EVENT, this.onAttachmentUpdatedFromView);
                }
            },
            async confirmAndRemoveAttachment(attachment) {
                const msg = this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.confirmRemoveMessage,
                    values: { fileName: attachment.AttachmentDefinitionName }
                });
                if (!(await this.$app.showConfirmationDialog(this.confirmRemoveDlgTitle, msg))) {
                    return;
                }

                this.tableLoading = true;
                try {
                    await this.controller.RemoveAttachment({
                        programId: this.ProgramId,
                        attachmentId: attachment.AttachmentId
                    });
                } finally {
                    await this._loadTableData();
                }
            },
            async getAttachmentDefinitions() {
                if (this.attachmentDefinitions) {
                    return;
                }
                try {
                    this.attachmentDefinitionsLoading = true;
                    const result = await this.controller.GetAttachmentDefinitions({
                        programId: this.ProgramId
                    });
                    this.attachmentDefinitions = result.Data.sort();
                } finally {
                    this.attachmentDefinitionsLoading = false;
                }
            },
            async onAddAttachment() {
                this.showAttachmentSelector = true;
                await this.getAttachmentDefinitions();
                if (this.hasAttachmentDefinitions) {
                    this.selectedAttachmentDefinitionId = this.attachmentDefinitions[0].PublicId;
                }
            },
            addAttachmentCancel() {
                this.showAttachmentSelector = false;
                this.selectedAttachmentDefinitionId = null;
            },
            async uploadAndSaveAttachment(file) {
                if (this.projectLockStore.isLocked) {
                    return;
                }

                const data = {
                    programId: this.ProgramId,
                    projectId: this.ProjectId,
                    attachmentDefinitionId: this.selectedAttachmentDefinitionId,
                    file
                };

                const result = await this.$app.sendXhr({
                    url: this.controller.ActionUrls.SaveAttachment,
                    data,
                    headers: this.$app.getXhrHeaders(),
                    captureXhr: (xhr) => (this.xhr = xhr),
                    contentType: XHR_CONTENT_TYPE.FormData
                });

                if (isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) {
                    this.$store.dispatch(PROJECT_FORM_TYPES.actions.afterUploadAttachment, {
                        uploadResult: result.Data,
                        attachmentDefinitionId: this.selectedAttachmentDefinitionId
                    });
                }

                this._loadTableData();
                this.showAttachmentSelector = false;
            },
            uploadCanceled() {
                if (this.xhr) {
                    this.xhr.abort();
                }
            },
            async viewHistory(attachment) {
                const result = await this.controller.GetAttachmentHistory({
                    programId: this.ProgramId,
                    projectId: this.ProjectId,
                    attachmentDefinitionId: attachment.AttachmentDefinitionId
                });
                const history = result.Data;
                if (!history || history.length === 0) {
                    return;
                }

                const title = this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.attachmentHistoryLink,
                    values: { fileName: attachment.AttachmentDefinitionName }
                });
                this.$app.showDialog({
                    title: title,
                    large: true,
                    customComponent: attachmentHistory,
                    customComponentOptions: {
                        attachment: attachment,
                        history: history
                    },
                    customComponentListeners: {
                        "download-attachment": this.downloadAttachment
                    },
                    buttons: [
                        {
                            text: this.closeText,
                            result: "CANCEL"
                        }
                    ]
                });
            },
            onAttachmentUpdatedFromView(event) {
                const eventProjectId = event.detail && event.detail.projectId;
                if (eventProjectId && eventProjectId === this.ProjectId) {
                    this._loadTableData();
                }
            },
            hasAttachmentDefinitions() {
                return this.attachmentDefinitions && this.attachmentDefinitions.length > 0;
            },
            getApprovalStatusDisplay,
            getApprovalStatusDisplayClass
        }
    };
</script>

<style lang="scss" scoped>
    :deep(.attachments-capsule-filename-cell-wrapper) {
        min-width: 150px;
    }

    :deep(.attachments-capsule-note-cell-wrapper) {
        min-width: 185px;
    }
</style>
